import type {ReactElement} from 'react';
import React from 'react';
import {BaseGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/base-generate-ai-subtitles-option/base-generate-ai-subtitles-option';
import {getAiTranscriptParamsForVideo, getSelectableItemDataForVideoData} from '@Libraries/ai-transcript.library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppDispatch} from '@/hooks';
import {clearGeneratedResults, setAiSubtitlesPanelItems} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {GA4EventName, GA4EventParam, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';

export function VideoItemGenerateAiSubtitlesOption(): ReactElement {
  const dispatch = useAppDispatch();
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  if (!activeItem) {
    throw new Error('Active item does not exist in video options');
  }
  if (!activeItem.isVideo()) {
    throw new Error(`Expected item to be of type Video - instead got ${activeItem.gitype}`);
  }
  const {getAiSubtitlesActionForItem, addAiTranscripts} = useAiTranscriptGeneration();

  const onGenerateAiSubtitles = async (): Promise<void> => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    const itemIdsInOrder = currentPage.items.getItemIdsInOrder();
    const videoItemIdsInOrder = itemIdsInOrder.filter((itemId) => {
      const item = currentPage.items.itemsHashMap[itemId];
      return item.isVideo();
    });
    const orderOfCurrentVideoItem = videoItemIdsInOrder.indexOf(activeItem.uid) + 1;
    const loopCycles = currentPage.getDuration() / (activeItem.endTime - activeItem.startTime);

    dispatch(setAiSubtitlesPanelItems([{...getSelectableItemDataForVideoData(activeItem.toObject(), loopCycles, orderOfCurrentVideoItem), isSelected: true}]));

    dispatch(clearGeneratedResults());

    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_CONTEXTUAL_MENU, {[GA4EventParamName.TYPE]: GA4EventParam.VIDEO});

    await addAiTranscripts([getAiTranscriptParamsForVideo(activeItem.toObject(), loopCycles)]);
  };

  const getLoopCycles = (): number => {
    return activeItem.page.getDuration() / (activeItem.endTime - activeItem.startTime);
  };

  return (
    <BaseGenerateAiSubtitlesOption
      ctaState={getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(activeItem.toObject(), getLoopCycles()))}
      className="spacing-m-t-2"
      onGenerateClicked={onGenerateAiSubtitles}
    />
  );
}
